import React, { useState } from 'react';
import { getPostgresClient } from '../services/postgresClient';

export interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** URL da API Postgres (mesma origem usada pelo board). */
  postgresApiUrl?: string;
}

/**
 * Modal para o usuário logado trocar a própria senha.
 * @param props - Controle de abertura e URL da API
 */
export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  postgresApiUrl,
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Reseta campos e mensagens ao fechar o modal.
   */
  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  /**
   * Envia nova senha para a API após validação local básica.
   * @param event - Submit do formulário
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!currentPassword.trim()) {
      setError('Informe a senha atual.');
      return;
    }
    if (newPassword.length < 8) {
      setError('A nova senha deve ter no mínimo 8 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('A confirmação não coincide com a nova senha.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('A nova senha deve ser diferente da senha atual.');
      return;
    }

    setIsSubmitting(true);
    try {
      const sb = getPostgresClient(postgresApiUrl);
      const result = await sb.auth.changePassword(currentPassword, newPassword);
      if (!result.ok) {
        setError(result.error ?? 'Não foi possível alterar a senha.');
        return;
      }
      setSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-labelledby="change-password-title"
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-[0_24px_64px_-16px_rgba(0,0,0,0.18)] w-full max-w-md overflow-hidden flex flex-col border border-slate-100 animate-in zoom-in-95 duration-200"
      >
        <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex justify-between items-start gap-3 shrink-0 bg-gradient-to-br from-primary-accent/5 to-white">
          <div className="flex items-start gap-3">
            <div className="size-10 rounded-xl bg-primary-accent/10 flex items-center justify-center text-primary-accent border border-primary-accent/20 shadow-sm">
              <span className="material-symbols-outlined !text-[22px]">key</span>
            </div>
            <div>
              <h3 id="change-password-title" className="text-base font-semibold text-slate-800 tracking-tight">
                Trocar senha
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">Informe a senha atual e defina uma nova.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 -mr-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
            aria-label="Fechar"
          >
            <span className="material-symbols-outlined !text-[20px]">close</span>
          </button>
        </div>

        {success ? (
          <div className="px-5 py-6 space-y-4">
            <div className="flex flex-col items-center text-center gap-2">
              <div className="size-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">
                <span className="material-symbols-outlined !text-[28px]">check_circle</span>
              </div>
              <p className="text-sm font-semibold text-slate-800">Senha alterada com sucesso!</p>
              <p className="text-[12px] text-slate-500">Use a nova senha no próximo acesso.</p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="w-full py-2.5 rounded-xl bg-primary-accent text-white text-sm font-semibold shadow-md shadow-primary-accent/20 hover:brightness-105 active:scale-[0.99] transition-all"
            >
              Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
            <label className="block">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Senha atual</span>
              <input
                autoFocus
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-primary-accent/20 focus:border-primary-accent/40"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Nova senha</span>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="Mínimo 8 caracteres"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-primary-accent/20 focus:border-primary-accent/40"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Confirmar nova senha</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-primary-accent/20 focus:border-primary-accent/40"
              />
            </label>

            {error && (
              <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" role="alert">
                {error}
              </p>
            )}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-2.5 rounded-xl bg-primary-accent text-white text-sm font-semibold shadow-md shadow-primary-accent/20 hover:brightness-105 active:scale-[0.99] transition-all disabled:opacity-60"
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
